import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import carritoFail from "../../../assets/image/xmarca 1.svg";
import "../../success/success-minimal.css";
import { useSelector } from "react-redux";
import { getOrderByIdApi } from "../../../api/api";
import { MAIN_API } from "../../../api/apilist";

const CancelFiservPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const token = useSelector((s) => s.auth?.accessToken);
	const [retrying, setRetrying] = useState(false);
	const [redirectPath, setRedirectPath] = useState("/event");

	const { orderId, status, message, responseCode, isReservation } =
		useMemo(() => {
			const sp = new URLSearchParams(location.search || "");
			return {
				isReservation: (sp.get("type") || "").toLowerCase() === "reservation",
				orderId:
					sp.get("orderId") ||
					sp.get("orderID") ||
					sp.get("order") ||
					sp.get("oid") ||
					"",
				status: (sp.get("status") || sp.get("state") || "").toLowerCase(),
				message: sp.get("message") || sp.get("error") || sp.get("reason") || "",
				responseCode: sp.get("respcode") || sp.get("code") || "",
			};
		}, [location.search]);

	const isMobile = useMemo(() => {
		if (typeof navigator === "undefined") return false;
		return /android|iphone|ipad|ipod/i.test(navigator.userAgent || "");
	}, []);

	const deepLinkUrl = useMemo(() => {
		const base = "zoco-tickets://payment/failed";
		const params = new URLSearchParams();
		if (orderId) params.set("orderId", orderId);
		const statusValue = status || "failed";
		if (statusValue) params.set("status", statusValue);
		const query = params.toString();
		return query ? `${base}?${query}` : base;
	}, [orderId, status]);

	const reservationReturn = useMemo(() => {
		try {
			const raw = sessionStorage.getItem("reservation:last");
			const parsed = raw ? JSON.parse(raw) : null;
			if (
				!["reservas", "canchas"].includes(parsed?.flow) ||
				!parsed?.reservationCode ||
				!orderId
			)
				return null;
			if (String(parsed.reservationCode) !== String(orderId)) return null;
			return parsed;
		} catch (_) {
			return null;
		}
	}, [orderId]);

	useEffect(() => {
		if (isReservation) {
			navigate(`/reservas/cancel${location.search || ""}`, { replace: true });
			return;
		}
		if (!reservationReturn) return;
		const base =
			reservationReturn.flow === "canchas" ? "/canchas" : "/reservas";
		navigate(`${base}/cancel${location.search || ""}`, { replace: true });
	}, [isReservation, reservationReturn, navigate, location.search]);

	const failureMessage = useMemo(() => {
		if (message) return message;
		if (status.includes("pending"))
			return "El pago quedo pendiente o se cancelo antes de confirmarse.";
		if (status) return "El pago no fue aprobado. Podes intentarlo nuevamente.";
		return "No pudimos procesar el pago, intenta de nuevo en unos minutos.";
	}, [message, status]);

	useEffect(() => {
		if (isReservation) return;
		let cancelled = false;

		const resolveRedirectTarget = async () => {
			try {
				const ctx = JSON.parse(
					localStorage.getItem("lastCheckoutCtx") || "null",
				);
				if (ctx?.eventId) {
					if (!cancelled) setRedirectPath(`/event/${ctx.eventId}`);
					return;
				}
			} catch (_) {}

			try {
				const pending = JSON.parse(
					localStorage.getItem("pendingCheckout") || "null",
				);
				if (pending?.eventId) {
					if (!cancelled) setRedirectPath(`/event/${pending.eventId}`);
					return;
				}
			} catch (_) {}

			if (!orderId) return;
			try {
				const res = await getOrderByIdApi(orderId);
				const info = res?.data?.info || {};
				const evId = info?.eventid?._id || info?.eventid || "";
				if (evId && !cancelled) setRedirectPath(`/event/${evId}`);
			} catch (_) {}
		};

		resolveRedirectTarget();
		return () => {
			cancelled = true;
		};
	}, [isReservation, orderId]);

	useEffect(() => {
		if (isReservation || !orderId) return;
		const resolvedStatus = status || "failed";
		const params = new URLSearchParams();
		params.set("oid", orderId);
		params.set("status", resolvedStatus);
		const isPending = resolvedStatus.includes("pending");
		if (!isPending) {
			params.set("approval_code", "N-FRONT");
			params.set("respcode", responseCode || "05");
		} else if (responseCode) {
			params.set("respcode", responseCode);
		}
		if (message) params.set("message", message);
		(async () => {
			try {
				const url = `${MAIN_API}/api/payment/fiserv/connect/return?${params.toString()}`;
				await fetch(url, { method: "GET" });
			} catch (_) {}
		})();
	}, [isReservation, orderId, status, message, responseCode]);

	useEffect(() => {
		if (isReservation) return undefined;
		const t = setTimeout(() => navigate(redirectPath, { replace: true }), 6000);
		return () => clearTimeout(t);
	}, [isReservation, navigate, redirectPath]);

	useEffect(() => {
		if (isReservation || !isMobile || reservationReturn) return;
		const t = setTimeout(() => {
			try {
				window.location.href = deepLinkUrl;
			} catch (_) {}
		}, 350);
		return () => clearTimeout(t);
	}, [deepLinkUrl, isMobile, isReservation, reservationReturn]);

	if (isReservation) return null;

	const retryPayment = async () => {
		try {
			setRetrying(true);
			const oid = orderId || localStorage.getItem("lastOrderId") || "";
			let eventId = "";
			let quantities = {};
			if (oid) {
				try {
					const res = await getOrderByIdApi(oid);
					const info = res?.data?.info || {};
					eventId = info?.eventid?._id || info?.eventid || "";
					if (Array.isArray(info?.items)) {
						for (const it of info.items) {
							const key = it?.ticketTypeId ?? it?.typeId ?? "DEFAULT";
							const q = Number(it?.quantity) || 1;
							quantities[key] = (quantities[key] || 0) + q;
						}
					} else if (info?.quantity) {
						quantities["DEFAULT"] = Number(info.quantity) || 1;
					}
				} catch {}
			}
			if (!eventId) {
				try {
					const ctx = JSON.parse(
						localStorage.getItem("lastCheckoutCtx") || "null",
					);
					if (ctx?.eventId) {
						eventId = ctx.eventId;
						quantities = ctx.quantities || {};
					}
				} catch {}
			}
			if (!eventId) {
				navigate(redirectPath);
				return;
			}
			try {
				localStorage.setItem(
					"pendingCheckout",
					JSON.stringify({ provider: "fiserv", eventId, quantities }),
				);
			} catch {}
			if (!token) {
				navigate("/login", { state: { from: `/event/${eventId}`, reason: "checkout" } });
			} else {
				navigate(`/event/${eventId}`);
			}
		} catch (e) {
			console.error(e);
			navigate(redirectPath);
		} finally {
			setRetrying(false);
		}
	};

	return (
		<div className="success-minimal">
			<div className="success-minimal-card">
				<img className="success-minimal-icon" src={carritoFail} alt="abrir" />
				<h2 className="error-minimal-title">Ups! Algo salio mal</h2>
				<p className="error-minimal-subtext">{failureMessage}</p>
				<button
					className="success-minimal-btn"
					onClick={retryPayment}
					disabled={retrying}
				>
					{retrying ? "Redirigiendo..." : "Reintentar pago"}
				</button>
				{isMobile ? (
					<button
						className="success-minimal-btn"
						onClick={() => {
							try {
								window.location.href = deepLinkUrl;
							} catch (_) {}
						}}
					>
						Abrir en la app
					</button>
				) : null}
				<div className="success-minimal-note">
					<span>
						<i>i</i> Si el problema persiste, contacta a{" "}
						<a href="/contact-us">
							<strong>nuestro soporte</strong>
						</a>
						.
					</span>
					{responseCode && (
						<span style={{ display: "block", marginTop: "0.5rem" }}>
							Codigo de referencia: <strong>{responseCode}</strong>
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export default CancelFiservPage;
